// ── CookieConsent.jsx ─────────────────────────────────────────────────────────
// Professional Cookie Consent Banner — shown after age verification & language
// selection are both complete.
//
// Features:
//  • Full bilingual support (AR / EN) — reads from LanguageContext.
//  • Three states: "pending", "accepted" (all), "rejected" (necessary only).
//  • Stores consent in localStorage so it survives across sessions.
//  • "Manage Preferences" modal with granular category toggles (Analytics,
//    Marketing, Functional) — Necessary cookies are always ON and locked.
//  • Design is fully consistent with the LeaksMercy dark theme (same palette,
//    animations, and card style used in AgeGate & LanguageSelector).
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../i18n/LanguageContext";
import "./CookieConsent.css";

// ── LocalStorage key ───────────────────────────────────────────────────────────
const COOKIE_CONSENT_KEY = "leaksmercy_cookie_consent";
const COOKIE_PREFS_KEY   = "leaksmercy_cookie_prefs";

// ── Default preference object ─────────────────────────────────────────────────
const DEFAULT_PREFS = {
  necessary: true,   // always true, cannot be disabled
  analytics: false,
  marketing: false,
  functional: false,
};

// ── Helpers ────────────────────────────────────────────────────────────────────
function getSavedConsent() {
  try {
    const v = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (v === "accepted" || v === "rejected" || v === "custom") return v;
  } catch {}
  return null; // not yet decided
}

function getSavedPrefs() {
  try {
    const raw = localStorage.getItem(COOKIE_PREFS_KEY);
    if (raw) return { ...DEFAULT_PREFS, ...JSON.parse(raw) };
  } catch {}
  return { ...DEFAULT_PREFS };
}

function saveConsent(decision, prefs) {
  try {
    localStorage.setItem(COOKIE_CONSENT_KEY, decision);
    localStorage.setItem(COOKIE_PREFS_KEY, JSON.stringify(prefs));
  } catch {}
}

// ── Inline translations (no dependency issue) ─────────────────────────────────
const T = {
  ar: {
    // Banner
    bannerTitle:      "نحن نحترم خصوصيتك",
    bannerSubtitle:   "نستخدم ملفات تعريف الارتباط (Cookies) لتحسين تجربتك وضمان عمل الموقع بشكل صحيح. يمكنك قبول جميعها أو تخصيص تفضيلاتك.",
    acceptAll:        "قبول الكل",
    rejectAll:        "الضرورية فقط",
    managePrefs:      "إدارة التفضيلات",
    learnMore:        "معرفة المزيد",
    // Manage modal
    modalTitle:       "إدارة ملفات تعريف الارتباط",
    modalSubtitle:    "اختر فئات الكوكيز التي توافق عليها. يمكنك تغيير هذه الإعدادات في أي وقت.",
    necessary:        "ضرورية",
    necessaryDesc:    "مطلوبة لعمل الموقع بشكل أساسي — تسجيل الدخول، الجلسة، الأمان. لا يمكن تعطيلها.",
    analytics:        "تحليلية",
    analyticsDesc:    "تساعدنا على فهم كيفية استخدامك للموقع لتحسين الأداء والمحتوى.",
    marketing:        "تسويقية",
    marketingDesc:    "تُستخدم لعرض إعلانات ملائمة لاهتماماتك وقياس فاعلية الحملات.",
    functional:       "وظيفية",
    functionalDesc:   "تحفظ تفضيلاتك مثل اللغة والإعدادات الشخصية لتجربة أفضل.",
    alwaysActive:     "نشط دائماً",
    savePrefs:        "حفظ التفضيلات",
    cancelBtn:        "إلغاء",
    // Accepted / Rejected mini-banner
    consentSaved:     "تم حفظ تفضيلاتك",
    // Cookie icon accessibility
    cookieIcon:       "أيقونة الكوكيز",
    // Legal / note
    legalNote:        "باستخدامك لهذا الموقع فأنت تقر بسياسة الخصوصية وشروط الاستخدام. هذا الموقع مخصص للبالغين +18.",
    // Policy link text
    privacyPolicy:    "سياسة الخصوصية",
  },
  en: {
    // Banner
    bannerTitle:      "We Respect Your Privacy",
    bannerSubtitle:   "We use cookies to enhance your experience and ensure the site works correctly. You can accept all or customize your preferences.",
    acceptAll:        "Accept All",
    rejectAll:        "Necessary Only",
    managePrefs:      "Manage Preferences",
    learnMore:        "Learn More",
    // Manage modal
    modalTitle:       "Manage Cookie Preferences",
    modalSubtitle:    "Choose which cookie categories you consent to. You can change these settings at any time.",
    necessary:        "Necessary",
    necessaryDesc:    "Required for basic site functionality — login, session, security. Cannot be disabled.",
    analytics:        "Analytics",
    analyticsDesc:    "Helps us understand how you use the site to improve performance and content.",
    marketing:        "Marketing",
    marketingDesc:    "Used to show relevant ads based on your interests and measure campaign effectiveness.",
    functional:       "Functional",
    functionalDesc:   "Saves your preferences such as language and personal settings for a better experience.",
    alwaysActive:     "Always Active",
    savePrefs:        "Save Preferences",
    cancelBtn:        "Cancel",
    // Accepted / Rejected mini-banner
    consentSaved:     "Your preferences have been saved",
    // Cookie icon accessibility
    cookieIcon:       "Cookie icon",
    // Legal / note
    legalNote:        "By using this site you acknowledge our Privacy Policy and Terms of Use. This site is for adults 18+ only.",
    // Policy link text
    privacyPolicy:    "Privacy Policy",
  },
};

// ── Cookie icon SVG ────────────────────────────────────────────────────────────
const CookieIcon = () => (
  <svg
    viewBox="0 0 40 40"
    fill="none"
    aria-hidden="true"
    className="cc-cookie-svg"
  >
    <circle cx="20" cy="20" r="18" fill="rgba(229,0,0,0.12)" stroke="#e50000" strokeWidth="1.8"/>
    <circle cx="13" cy="14" r="2.5" fill="#e50000" opacity="0.8"/>
    <circle cx="25" cy="12" r="1.8" fill="#e50000" opacity="0.65"/>
    <circle cx="27" cy="24" r="2.2" fill="#e50000" opacity="0.75"/>
    <circle cx="16" cy="26" r="1.6" fill="#e50000" opacity="0.6"/>
    <circle cx="21" cy="19" r="1.4" fill="#e50000" opacity="0.55"/>
    <circle cx="12" cy="22" r="1.2" fill="#e50000" opacity="0.5"/>
    <circle cx="28" cy="16" r="1.0" fill="#e50000" opacity="0.45"/>
    {/* Bite mark */}
    <path
      d="M34 10 Q38 14 34 18"
      stroke="#0a0a0a"
      strokeWidth="5"
      strokeLinecap="round"
      fill="none"
    />
  </svg>
);

// ── Toggle Switch ──────────────────────────────────────────────────────────────
const ToggleSwitch = ({ checked, onChange, disabled, id }) => (
  <button
    role="switch"
    aria-checked={checked}
    aria-disabled={disabled}
    id={id}
    onClick={() => !disabled && onChange(!checked)}
    className={`cc-toggle${checked ? " cc-toggle--on" : ""}${disabled ? " cc-toggle--disabled" : ""}`}
    type="button"
  >
    <span className="cc-toggle-thumb" />
  </button>
);

// ── Category Row ───────────────────────────────────────────────────────────────
const CategoryRow = ({ label, desc, checked, onChange, disabled, alwaysLabel, id }) => (
  <div className="cc-category-row">
    <div className="cc-category-info">
      <span className="cc-category-label">{label}</span>
      <span className="cc-category-desc">{desc}</span>
    </div>
    <div className="cc-category-control">
      {disabled ? (
        <span className="cc-always-active">{alwaysLabel}</span>
      ) : (
        <ToggleSwitch
          checked={checked}
          onChange={onChange}
          disabled={disabled}
          id={id}
        />
      )}
    </div>
  </div>
);

// ── Manage Preferences Modal ───────────────────────────────────────────────────
const ManageModal = ({ lang, prefs, onSave, onClose }) => {
  const t = T[lang];
  const [localPrefs, setLocalPrefs] = useState({ ...prefs });

  const toggle = (key) =>
    setLocalPrefs((p) => ({ ...p, [key]: !p[key] }));

  return (
    <div
      className="cc-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={t.modalTitle}
      dir={lang === "ar" ? "rtl" : "ltr"}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="cc-modal-card">
        {/* Header */}
        <div className="cc-modal-header">
          <div className="cc-modal-title-row">
            <CookieIcon />
            <div>
              <h2 className="cc-modal-title">{t.modalTitle}</h2>
              <p className="cc-modal-subtitle">{t.modalSubtitle}</p>
            </div>
          </div>
          <button
            className="cc-modal-close"
            onClick={onClose}
            aria-label="Close"
            type="button"
          >
            <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <line x1="3" y1="3" x2="13" y2="13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              <line x1="13" y1="3" x2="3" y2="13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        {/* Divider */}
        <div className="cc-modal-divider" />

        {/* Categories */}
        <div className="cc-categories">
          <CategoryRow
            id="cc-necessary"
            label={t.necessary}
            desc={t.necessaryDesc}
            checked={true}
            onChange={() => {}}
            disabled={true}
            alwaysLabel={t.alwaysActive}
          />
          <CategoryRow
            id="cc-analytics"
            label={t.analytics}
            desc={t.analyticsDesc}
            checked={localPrefs.analytics}
            onChange={(v) => toggle("analytics")}
            disabled={false}
            alwaysLabel={t.alwaysActive}
          />
          <CategoryRow
            id="cc-marketing"
            label={t.marketing}
            desc={t.marketingDesc}
            checked={localPrefs.marketing}
            onChange={(v) => toggle("marketing")}
            disabled={false}
            alwaysLabel={t.alwaysActive}
          />
          <CategoryRow
            id="cc-functional"
            label={t.functional}
            desc={t.functionalDesc}
            checked={localPrefs.functional}
            onChange={(v) => toggle("functional")}
            disabled={false}
            alwaysLabel={t.alwaysActive}
          />
        </div>

        {/* Footer */}
        <div className="cc-modal-footer">
          <button
            className="cc-modal-cancel"
            onClick={onClose}
            type="button"
          >
            {t.cancelBtn}
          </button>
          <button
            className="cc-modal-save"
            onClick={() => onSave(localPrefs)}
            type="button"
          >
            {t.savePrefs}
          </button>
        </div>

        {/* Legal note */}
        <p className="cc-modal-legal">
          {t.legalNote}{" "}
          <Link to="/privacy-policy" className="cc-modal-legal-link">{t.privacyPolicy}</Link>
          {" · "}
          <Link to="/terms-of-service" className="cc-modal-legal-link">{lang === "ar" ? "شروط الاستخدام" : "Terms of Service"}</Link>
        </p>
      </div>
    </div>
  );
};

// ── Main CookieConsent Component ───────────────────────────────────────────────
const CookieConsent = () => {
  const { lang } = useLanguage();
  const t = T[lang];
  const isRTL = lang === "ar";

  // "pending" | "accepted" | "rejected" | "custom" | "saved"
  const [status, setStatus]       = useState(() => getSavedConsent() ?? "pending");
  const [prefs, setPrefs]         = useState(() => getSavedPrefs());
  const [showModal, setShowModal] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  // Animate in after a short delay when status is pending
  useEffect(() => {
    if (status === "pending") {
      const tid = setTimeout(() => setIsVisible(true), 900);
      return () => clearTimeout(tid);
    }
  }, [status]);

  // Prevent body scroll when manage modal is open
  useEffect(() => {
    if (showModal) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [showModal]);

  const dismiss = useCallback((decision, resolvedPrefs) => {
    setIsExiting(true);
    setTimeout(() => {
      setStatus(decision);
      setPrefs(resolvedPrefs);
      saveConsent(decision, resolvedPrefs);
      setIsExiting(false);
      setIsVisible(false);
    }, 400);
  }, []);

  const handleAcceptAll = useCallback(() => {
    const all = { necessary: true, analytics: true, marketing: true, functional: true };
    dismiss("accepted", all);
  }, [dismiss]);

  const handleRejectAll = useCallback(() => {
    const necessary = { ...DEFAULT_PREFS, necessary: true };
    dismiss("rejected", necessary);
  }, [dismiss]);

  const handleSaveCustom = useCallback((customPrefs) => {
    setShowModal(false);
    const saved = { ...customPrefs, necessary: true };
    dismiss("custom", saved);
  }, [dismiss]);

  // Already decided — don't render banner
  if (status !== "pending") return null;

  // Not yet visible (fade-in delay)
  if (!isVisible && !isExiting) return null;

  return (
    <>
      {/* ── Cookie Banner ─────────────────────────────────────────────────── */}
      <div
        className={`cc-banner${isExiting ? " cc-banner--exiting" : " cc-banner--visible"}`}
        dir={isRTL ? "rtl" : "ltr"}
        role="region"
        aria-label={t.bannerTitle}
      >
        {/* Background glow */}
        <div className="cc-banner-glow" aria-hidden="true" />

        {/* Left: icon + text */}
        <div className="cc-banner-left">
          <div className="cc-banner-icon-wrap" aria-label={t.cookieIcon}>
            <CookieIcon />
          </div>
          <div className="cc-banner-text">
            <h3 className="cc-banner-title">{t.bannerTitle}</h3>
            <p className="cc-banner-subtitle">{t.bannerSubtitle}</p>
            <button
              className="cc-learn-more"
              onClick={() => setShowModal(true)}
              type="button"
            >
              {t.learnMore} →
            </button>
          </div>
        </div>

        {/* Right: action buttons */}
        <div className="cc-banner-actions">
          <button
            className="cc-btn cc-btn--accept"
            onClick={handleAcceptAll}
            type="button"
          >
            {t.acceptAll}
          </button>
          <button
            className="cc-btn cc-btn--reject"
            onClick={handleRejectAll}
            type="button"
          >
            {t.rejectAll}
          </button>
          <button
            className="cc-btn cc-btn--manage"
            onClick={() => setShowModal(true)}
            type="button"
          >
            {t.managePrefs}
          </button>
        </div>
      </div>

      {/* ── Manage Preferences Modal ───────────────────────────────────────── */}
      {showModal && (
        <ManageModal
          lang={lang}
          prefs={prefs}
          onSave={handleSaveCustom}
          onClose={() => setShowModal(false)}
        />
      )}
    </>
  );
};

export default CookieConsent;

// ── Public utility: read cookie consent status & preferences ──────────────────
// Other components can import these helpers to conditionally load scripts.
export function getCookieConsent() {
  return getSavedConsent(); // "accepted" | "rejected" | "custom" | null
}

export function getCookiePrefs() {
  return getSavedPrefs(); // { necessary, analytics, marketing, functional }
}
